<template>
  <div class="container" v-iod-redirect>
    <NoAccessError v-if="!hasAccess" />
    <div v-else-if="statement">
      <GbBreadcrumbs
        v-if="folderUuid"
        :items="breadcrumbItems"
        class="mt-6 -mb-4"
      />
      <div class="flex justify-center">
        <h1 class="text-display-xl font-medium text-gray-900 mt-8 mb-6">
          Impact of Disability Statement
        </h1>
      </div>
      <div class="flex flex-col">
        <router-view :statement="statement" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useImpactOfDisabilityStore } from '@/impact-of-disability/impact-of-disability.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import GbBreadcrumbs from '@/components/GbBreadcrumbs.vue'
import NoAccessError from '@/components/NoAccessError.vue'
import { useRoute, useRouter } from 'vue-router'
import type { LibraryImpactOfDisability } from '@/library/library_types'
import { useLibraryStore } from '@/library/library.store'

const impactOfDisabilityStore = useImpactOfDisabilityStore()
const libraryStore = useLibraryStore()
const visibilityStore = useVisibilityStore()
const route = useRoute()
const router = useRouter()

const hasAccess = ref(true)

const statementUUID = computed(() => route.params.statement_uuid as string)
const statement = computed(() => {
  return impactOfDisabilityStore.savedStatements.find((s: LibraryImpactOfDisability) => s.uuid === statementUUID.value)
})
const folderUuid = computed(() => statement.value?.student_uuid || '')

watch(folderUuid, async (uuid) => {
  if (uuid) {
    if (!libraryStore.folders.some(f => f.uuid === uuid)) {
      await libraryStore.fetchResourcesForFolder({ folderUUID: uuid, params: {} })
      libraryStore.folders.push(libraryStore.currentFolder)
    } else {
      libraryStore.setCurrentFolderByUuid(uuid)
    }
  }
})

const fetchStatementData = async () => {
  if (statementUUID.value) {
    hasAccess.value = true
    try {
      await impactOfDisabilityStore.fetchStatementByUUID(statementUUID.value)
    } catch (error: any) {
      if (error?.status_code === 403 || error?.status_code === 404) {
        hasAccess.value = false
      } else {
        throw error
      }
    }
  }
}

onMounted(async () => {  
  await fetchStatementData()
})

watch(statementUUID, fetchStatementData)

const breadcrumbItems = computed(() => {
  return [
    {
      label: 'Library: All Folders',
      to: { name: 'Library' }
    },
    {
      label: libraryStore.currentFolder?.name || statement.value?.student_name || '',
      icon: 'folder',
      to: { 
        name: 'LibraryFolder', 
        params: { uuid: folderUuid.value } 
      }
    },
    {
      label: `Impact of Disability: ${statement.value?.disability || 'Statement'}`
    }
  ]
})
</script>
